// BULKVENDOR1 + BULKEDIT1 (docs/build-briefs/BULKEDIT1-inventory-bulk-edit.md; design/style-guide.md sections 7, 8, 10, 12). The short
// sheet behind each button of the Items action strip (Vendor, Shelf tier, Active / Inactive, 86 / Back in stock). Built at runtime and appended to the page, not written into index.html, because check:chrome-ratchet
// refuses a new scrim-shaped overlay there (the price history sheet does the same). The choice is a radio list of a fixed vocabulary
// (the active vendors, the shelf tiers, or two fixed words), never typed. The caller owns the calls (options, apply); this draws the
// steps: pick, confirm, apply, result. Names go in with textContent only. Sign-out closes it through closeBulkEditSheet().

import {
  BULK_EDIT_COPY,
  addBatch,
  emptyTotals,
  offersAddedSentence,
  resultSentence,
  stoppedSentence,
  toBatches,
  type BulkEditBatchResult,
  type BulkEditOption,
  type BulkEditSpec,
  type BulkEditTotals,
} from "./bulkEditModel.ts";

export interface BulkEditSheetItem {
  id: string;
  name: string;
}

export type BulkEditLoad =
  | { ok: true; options: BulkEditOption[] }
  | { ok: false; message: string };

export type BulkEditApply =
  | { ok: true; result: BulkEditBatchResult }
  | { ok: false; message: string };

export interface BulkEditSheetOptions {
  spec: BulkEditSpec;
  items: readonly BulkEditSheetItem[];
  locationName: string | undefined;
  /** The choices. The two fixed vocabularies resolve at once. */
  loadOptions: () => Promise<BulkEditLoad>;
  applyBatch: (optionValue: string, itemIds: string[]) => Promise<BulkEditApply>;
  /** Called when the sheet closes. `failedIds` are the items that could not be changed (they stay selected); `touched` is true
   * when any batch ran, so the caller reloads the list. */
  onClosed: (outcome: { touched: boolean; failedIds: string[] }) => void;
}

const SCRIM_ID = "bulk-edit-scrim";

let scrim: HTMLElement | undefined;
let generation = 0;
let closeOutcome: { touched: boolean; failedIds: string[] } = { touched: false, failedIds: [] };
let onClosedCallback: BulkEditSheetOptions["onClosed"] | undefined;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
  testId?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  if (testId !== undefined) node.dataset["testid"] = testId;
  return node;
}

export function isBulkEditSheetOpen(): boolean {
  return scrim !== undefined;
}

/** Closes and removes the sheet. Safe when it was never opened. */
export function closeBulkEditSheet(): void {
  if (scrim === undefined) return;
  generation += 1;
  scrim.remove();
  scrim = undefined;
  const after = onClosedCallback;
  const outcome = closeOutcome;
  onClosedCallback = undefined;
  closeOutcome = { touched: false, failedIds: [] };
  after?.(outcome);
}

export function openBulkEditSheet(options: BulkEditSheetOptions): void {
  scrim?.remove();
  generation += 1;
  const mine = generation;
  closeOutcome = { touched: false, failedIds: [] };
  onClosedCallback = options.onClosed;

  const root = el("div", "scrim", undefined, SCRIM_ID);
  root.id = SCRIM_ID;
  const sheet = el("div", "sheet bulk-edit-sheet", undefined, "bulk-edit-sheet");
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-modal", "true");
  sheet.setAttribute("aria-labelledby", "bulk-edit-title");

  const header = el("div", "sheet-header");
  const row = el("div", "sheet-title-row");
  const title = el("span", "sheet-title", options.spec.title, "bulk-edit-title");
  title.id = "bulk-edit-title";
  const x = el("button", "btn-icon", "✕", "bulk-edit-close");
  x.type = "button";
  x.setAttribute("aria-label", "Close");
  row.append(title, x);
  header.append(row);

  const body = el("div", "bv-body", undefined, "bulk-edit-body");
  const foot = el("div", "sheet-footer bv-footer");
  const note = el("p", "admin-field-help bv-reason", "", "bulk-edit-reason");
  const buttons = el("div", "bv-buttons");
  const cancel = el("button", "btn-secondary", BULK_EDIT_COPY.cancel, "bulk-edit-cancel");
  cancel.type = "button";
  const primary = el("button", "btn-primary", options.spec.applyIdle, "bulk-edit-apply");
  primary.type = "button";
  buttons.append(cancel, primary);
  foot.append(note, buttons);
  sheet.append(header, body, foot);
  root.append(sheet);
  scrim = root;

  let choices: BulkEditOption[] = [];
  let pickedId: string | undefined;
  let busy = false;
  let finished = false;

  const alive = (): boolean => generation === mine && scrim === root;
  x.addEventListener("click", () => {
    if (!busy) closeBulkEditSheet();
  });
  cancel.addEventListener("click", () => {
    if (!busy) closeBulkEditSheet();
  });
  root.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !busy) closeBulkEditSheet();
  });

  const picked = (): BulkEditOption | undefined => choices.find((choice) => choice.value === pickedId);

  function paintFooter(): void {
    primary.disabled = busy || pickedId === undefined;
    const choice = picked();
    primary.textContent = choice === undefined ? options.spec.applyIdle : options.spec.apply(choice, options.items.length);
    note.textContent = pickedId === undefined && !busy && !finished ? options.spec.pickPrompt : "";
    cancel.disabled = busy;
  }

  function paintPick(): void {
    body.replaceChildren();
    if (choices.length === 0) {
      body.append(el("p", "tabs-empty", options.spec.noOptions, "bulk-edit-empty"));
      paintFooter();
      primary.disabled = true;
      return;
    }
    const list = el("div", "bv-vendors", undefined, "bulk-edit-list");
    list.setAttribute("role", "radiogroup");
    list.setAttribute("aria-label", options.spec.groupLabel);
    for (const entry of choices) {
      const label = el("label", "bv-vendor", undefined, "bulk-edit-option");
      const input = el("input");
      input.type = "radio";
      input.name = "bulk-edit-choice";
      input.value = entry.value;
      input.checked = entry.value === pickedId;
      input.dataset["testid"] = "bulk-edit-radio";
      input.addEventListener("change", () => {
        pickedId = entry.value;
        paintPick();
      });
      label.append(input, el("span", "bv-vendor-name", entry.label));
      list.append(label);
    }
    body.append(list);
    const choice = picked();
    if (choice !== undefined) {
      body.append(el("p", "bv-confirm", options.spec.confirm(choice, options.items.length, options.locationName), "bulk-edit-confirm"));
      if (options.spec.note !== "") body.append(el("p", "admin-field-help", options.spec.note, "bulk-edit-note"));
    }
    paintFooter();
  }

  function paintResult(totals: BulkEditTotals, stopped: string): void {
    finished = true;
    body.replaceChildren();
    const sentence = resultSentence(totals);
    const good = totals.failed.length === 0 && stopped === "";
    const head = el("p", good ? "bv-result bv-result--ok" : "bv-result bv-result--warn", `${good ? "✓ " : "! "}${sentence}`, "bulk-edit-result");
    head.setAttribute("role", "status");
    body.append(head);
    const added = options.spec.kind === "vendor" ? offersAddedSentence(totals) : "";
    if (added !== "") body.append(el("p", "admin-field-help", added, "bulk-edit-result-added"));
    if (stopped !== "") body.append(el("p", "bv-result bv-result--warn", `! ${stopped}`, "bulk-edit-stopped"));
    if (totals.failed.length > 0) {
      const list = el("ul", "bv-failed", undefined, "bulk-edit-failed");
      for (const failure of totals.failed) {
        const item = el("li", undefined, undefined, "bulk-edit-failed-item");
        item.append(el("strong", undefined, failure.itemName), ` ${failure.reason}`);
        list.append(item);
      }
      body.append(list);
    }
    closeOutcome = { touched: true, failedIds: totals.failed.map((failure) => failure.liquorProductId) };
    buttons.replaceChildren();
    if (stopped !== "") {
      cancel.disabled = false;
      primary.disabled = false;
      primary.textContent = options.spec.applyIdle;
      const retry = el("button", "btn-secondary", options.spec.applyIdle, "bulk-edit-resume");
      retry.type = "button";
      retry.addEventListener("click", () => {
        finished = false;
        buttons.replaceChildren(cancel, primary);
        paintPick();
      });
      const done = el("button", "btn-primary", BULK_EDIT_COPY.done, "bulk-edit-done");
      done.type = "button";
      done.addEventListener("click", () => closeBulkEditSheet());
      buttons.append(retry, done);
    } else {
      const done = el("button", "btn-primary", BULK_EDIT_COPY.done, "bulk-edit-done");
      done.type = "button";
      done.addEventListener("click", () => closeBulkEditSheet());
      buttons.append(done);
      done.focus();
    }
    note.textContent = "";
  }

  async function apply(): Promise<void> {
    if (busy || pickedId === undefined) return;
    busy = true;
    const optionValue = pickedId;
    const batches = toBatches(options.items.map((item) => item.id));
    let totals = emptyTotals();
    let doneCount = 0;
    let stopped = "";
    for (const batch of batches) {
      paintFooter();
      body.replaceChildren(
        el("p", "bv-progress", `${options.spec.applying}: ${doneCount} of ${options.items.length}`, "bulk-edit-progress"),
      );
      const answer = await options.applyBatch(optionValue, batch);
      if (!alive()) return;
      closeOutcome = { touched: true, failedIds: closeOutcome.failedIds };
      if (!answer.ok) {
        stopped = stoppedSentence(doneCount, options.items.length, answer.message, options.spec.again);
        break;
      }
      totals = addBatch(totals, answer.result);
      doneCount += batch.length;
    }
    busy = false;
    paintResult(totals, stopped);
  }
  primary.addEventListener("click", () => void apply());

  function load(): void {
    body.replaceChildren(el("p", "tabs-loading", options.spec.loadingOptions, "bulk-edit-loading"));
    primary.disabled = true;
    void options.loadOptions().then((loaded) => {
      if (!alive()) return;
      if (!loaded.ok) {
        body.replaceChildren();
        const failed = el("div", undefined, undefined, "bulk-edit-load-error");
        failed.append(el("p", "tabs-empty", `${options.spec.loadFailed} ${loaded.message}`));
        const retry = el("button", "btn-secondary", BULK_EDIT_COPY.tryAgain, "bulk-edit-retry");
        retry.type = "button";
        retry.addEventListener("click", load);
        failed.append(retry);
        body.append(failed);
        return;
      }
      choices = loaded.options;
      paintPick();
    });
  }

  document.body.append(root);
  paintFooter();
  cancel.focus();
  load();
}
